import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { 
  User, Mail, Phone, Target, Scale, Ruler, Save, Loader2, CalendarDays, Clock, Cake,
  Dumbbell, Flame, Heart, Zap, Check, LogOut, MapPin, FileText, Leaf, Utensils, Pencil
} from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { unitLabel } from '../strength/units';
import { useWeightUnit } from '../../hooks/useWeightUnit';

const fitnessGoalsOptions = [
  { id: 'weight_loss', label: 'Weight Loss', icon: Flame },
  { id: 'muscle_building', label: 'Muscle Building', icon: Dumbbell },
  { id: 'endurance', label: 'Endurance', icon: Heart },
  { id: 'general_fitness', label: 'General Fitness', icon: Target },
  { id: 'strength', label: 'Strength', icon: Zap }
];

const experienceLevels = [
  { id: 'beginner', label: 'Athlete' },
  { id: 'intermediate', label: 'Champion' },
  { id: 'advanced', label: 'Warrior' }
];

const dietaryOptions = [
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'pescatarian', label: 'Pescatarian' },
  { id: 'gluten_free', label: 'Gluten-Free' },
  { id: 'dairy_free', label: 'Dairy-Free' },
  { id: 'nut_free', label: 'Nut Allergy' },
  { id: 'keto', label: 'Keto / Low-Carb' },
  { id: 'halal', label: 'Halal' },
  { id: 'kosher', label: 'Kosher' }
];

export const ProfileEditForm = ({
  user,
  formData,
  onFormChange,
  selectedGoals,
  onToggleGoal,
  experienceLevel,
  onExperienceLevelChange,
  dietaryRestrictions = [],
  onToggleDietaryRestriction = () => {},
  customDietaryNotes = '',
  onCustomDietaryNotesChange = () => {},
  goalDate,
  onDateSelect,
  calendarOpen,
  onCalendarOpenChange,
  daysUntilGoal,
  loading,
  onSubmit,
  onLogout,
  weeklyGoal = 5,
  onWeeklyGoalChange = () => {}
}) => {
  const { unit: weightUnit } = useWeightUnit();
  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2 }}
      className="relative lg:col-span-2 rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 shadow-[0_8px_30px_rgba(0,0,0,0.35)] overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-primary/8 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-primary/5 blur-3xl" />

      <div className="relative p-6 sm:p-8">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-1 mb-3">
          <Pencil className="h-3 w-3" /> Edit
        </div>
        <h3 className="font-heading text-2xl sm:text-3xl uppercase tracking-wider leading-tight bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent mb-6">
          Edit Profile
        </h3>
      
      <form onSubmit={onSubmit} className="space-y-6">
        {/* Personal Info Section */}
        <div className="pb-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_currentColor]" />
            <h4 className="font-heading text-xs sm:text-sm text-white uppercase tracking-[0.2em]">Personal Information</h4>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                <User className="h-4 w-4 text-primary" />
                Full Name
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
                className="h-12 bg-black/60 border-white/10 focus:border-primary text-white rounded-lg"
                data-testid="profile-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                Email
              </Label>
              <Input
                value={user?.email}
                disabled
                className="h-12 bg-black/40 border-white/10 text-muted-foreground rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="age" className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                <Cake className="h-4 w-4 text-primary" />
                Age
              </Label>
              <Input
                id="age"
                name="age"
                type="number"
                min="13"
                max="120"
                value={formData.age}
                onChange={(e) => onFormChange({ ...formData, age: e.target.value })}
                placeholder="e.g., 28"
                className="h-12 bg-black/60 border-white/10 focus:border-primary text-white rounded-lg"
                data-testid="profile-age-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location" className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Location
              </Label>
              <Input
                id="location"
                name="location"
                value={formData.location || ''}
                onChange={(e) => onFormChange({ ...formData, location: e.target.value })}
                placeholder="e.g., Los Angeles, CA"
                className="h-12 bg-black/60 border-white/10 focus:border-primary text-white rounded-lg"
                data-testid="profile-location-input"
              />
            </div>
          </div>

          {/* Phone number — used for SMS password reset (US numbers only).
              Stored as a US-formatted display string here; ProfilePage
              normalizes to E.164 on save. */}
          <div className="mt-4 space-y-2">
            <Label htmlFor="phone" className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" />
              Phone (for SMS password reset)
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              inputMode="tel"
              value={formData.phone || ''}
              onChange={(e) => {
                // Format as (XXX) XXX-XXXX while typing for readability
                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                let formatted = digits;
                if (digits.length >= 7) formatted = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                else if (digits.length >= 4) formatted = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
                else if (digits.length > 0) formatted = digits;
                onFormChange({ ...formData, phone: formatted });
              }}
              placeholder="(555) 555-5555"
              className="h-12 bg-black/60 border-white/10 focus:border-primary text-white rounded-lg"
              data-testid="profile-phone-input"
            />
            <p className="text-xs text-muted-foreground">US numbers only. Optional, but needed if you want SMS password recovery.</p>
          </div>

          {/* SMS workout-reminder opt-in — only meaningful when a phone is
              saved. Disabled (with a hint) until one is. We text once at noon
              ET when a user has been silent for 5+ days; cooldown 5 days. */}
          {(() => {
            const hasPhone = !!(formData.phone && formData.phone.replace(/\D/g, '').length === 10);
            const enabled = !!formData.sms_reminders_enabled;
            return (
              <div className="mt-4 p-4 rounded-2xl bg-white/[0.02] border border-white/10">
                <label className={`flex items-start gap-3 ${hasPhone ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
                  <input
                    type="checkbox"
                    disabled={!hasPhone}
                    checked={enabled}
                    onChange={(e) => onFormChange({ ...formData, sms_reminders_enabled: e.target.checked })}
                    className="mt-1 h-5 w-5 rounded border-border bg-black accent-primary"
                    data-testid="sms-reminders-toggle"
                  />
                  <div className="flex-1">
                    <p className="text-white font-medium flex items-center gap-2">
                      Text me workout reminders
                      {enabled && <span className="text-[10px] font-semibold bg-primary/[0.1] text-primary border border-primary/30 px-2 py-0.5 rounded-full uppercase tracking-wider">ON</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      We'll text you once if you haven't logged a workout in 5+ days. Max ~1 message per week. Reply STOP to opt out.
                      {!hasPhone && <span className="block text-yellow-400 mt-1">Add a phone number above to enable.</span>}
                    </p>
                  </div>
                </label>
              </div>
            );
          })()}

          {/* Bio Section */}
          <label className="mt-4 p-4 rounded-2xl bg-white/[0.02] border border-white/10 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.assignment_emails_enabled !== false}
              onChange={(e) => onFormChange({ ...formData, assignment_emails_enabled: e.target.checked })}
              className="mt-1 h-5 w-5 rounded border-border bg-black accent-primary"
              data-testid="assignment-emails-toggle"
            />
            <div className="flex-1">
              <p className="text-white font-medium">Email me when I'm assigned a workout</p>
              <p className="text-xs text-muted-foreground mt-1">Get an email whenever your coach assigns you a new personalized workout.</p>
            </div>
          </label>

          {/* Bio Section */}
          <div className="mt-6 space-y-2">
            <Label htmlFor="bio" className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              Bio
            </Label>
            <Textarea
              id="bio"
              name="bio"
              value={formData.bio || ''}
              onChange={(e) => onFormChange({ ...formData, bio: e.target.value })}
              placeholder="Tell us a little about yourself and your fitness journey..."
              className="min-h-[100px] bg-black/60 border-white/10 focus:border-primary text-white resize-none rounded-lg"
              maxLength={300}
              data-testid="profile-bio-input"
            />
            <p className="text-xs text-muted-foreground text-right">{(formData.bio || '').length}/300 characters</p>
          </div>
        </div>

        {/* Body Metrics Section */}
        <div className="pb-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_currentColor]" />
            <h4 className="font-heading text-xs sm:text-sm text-white uppercase tracking-[0.2em]">Body Metrics</h4>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="weight" className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                <Scale className="h-4 w-4 text-primary" />
                Current Weight ({unitLabel(weightUnit)})
              </Label>
              <Input
                id="weight"
                name="weight"
                type="number"
                step="0.1"
                value={formData.weight}
                onChange={(e) => onFormChange({ ...formData, weight: e.target.value })}
                placeholder="e.g., 175"
                className="h-12 bg-black/60 border-white/10 focus:border-primary text-white rounded-lg"
                data-testid="profile-weight-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="height" className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                <Ruler className="h-4 w-4 text-primary" />
                Height (inches)
              </Label>
              <Input
                id="height"
                name="height"
                type="number"
                step="0.1"
                value={formData.height}
                onChange={(e) => onFormChange({ ...formData, height: e.target.value })}
                placeholder="e.g., 70"
                className="h-12 bg-black/60 border-white/10 focus:border-primary text-white rounded-lg"
                data-testid="profile-height-input"
              />
            </div>
          </div>
        </div>

        {/* Fitness Goals Section */}
        <div className="pb-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 mb-2">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_currentColor]" />
            <h4 className="font-heading text-xs sm:text-sm text-white uppercase tracking-[0.2em]">Your Fitness Goals</h4>
          </div>
          <p className="text-muted-foreground text-sm mb-4">Select all goals that apply to you</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {fitnessGoalsOptions.map((goal) => {
              const Icon = goal.icon;
              const isSelected = selectedGoals.includes(goal.id);
              return (
                <button
                  key={goal.id}
                  type="button"
                  onClick={() => onToggleGoal(goal.id)}
                  data-testid={`profile-goal-${goal.id}`}
                  className={`relative rounded-2xl p-3 border transition-all flex flex-col items-center gap-2 text-center
                    ${isSelected 
                      ? 'bg-primary/[0.1] border-primary/50 shadow-[0_0_20px_rgba(250,204,21,0.08)]' 
                      : 'bg-white/[0.02] border-white/10 hover:border-primary/30 hover:bg-white/[0.04]'}`}
                >
                  <Icon className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className={`text-[11px] uppercase tracking-wider ${isSelected ? 'text-white' : 'text-muted-foreground'}`}>
                    {goal.label}
                  </span>
                  {isSelected && (
                    <span className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-2.5 w-2.5 text-black" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Experience Level Section */}
        <div className="pb-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_currentColor]" />
            <h4 className="font-heading text-xs sm:text-sm text-white uppercase tracking-[0.2em]">Experience Level</h4>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {experienceLevels.map((level) => {
              const isSelected = experienceLevel === level.id;
              return (
                <button
                  key={level.id}
                  type="button"
                  onClick={() => onExperienceLevelChange(level.id)}
                  data-testid={`profile-experience-${level.id}`}
                  className={`relative rounded-2xl p-4 border transition-all flex items-center justify-center gap-2
                    ${isSelected 
                      ? 'bg-primary/[0.1] border-primary/50 shadow-[0_0_20px_rgba(250,204,21,0.08)]' 
                      : 'bg-white/[0.02] border-white/10 hover:border-primary/30 hover:bg-white/[0.04]'}`}
                >
                  <span className={`font-heading uppercase tracking-wider text-sm ${isSelected ? 'text-white' : 'text-muted-foreground'}`}>
                    {level.label}
                  </span>
                  {isSelected && (
                    <span className="h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-2.5 w-2.5 text-black" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Weekly Workout Goal Section */}
        <div className="pb-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 mb-2">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_currentColor]" />
            <h4 className="font-heading text-xs sm:text-sm text-white uppercase tracking-[0.2em]">Weekly Workout Goal</h4>
          </div>
          <p className="text-muted-foreground text-sm mb-4">
            How many days a week do you want to exercise? Progress resets every Monday.
          </p>
          <div className="grid grid-cols-7 gap-1.5">
            {[1, 2, 3, 4, 5, 6, 7].map((n) => {
              const isSelected = weeklyGoal === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => onWeeklyGoalChange(n)}
                  data-testid={`profile-weekly-goal-${n}`}
                  className={`rounded-2xl p-3 border transition-all flex flex-col items-center justify-center gap-0.5
                    ${isSelected
                      ? 'bg-primary/[0.1] border-primary/50 shadow-[0_0_20px_rgba(250,204,21,0.08)]'
                      : 'bg-white/[0.02] border-white/10 hover:border-primary/30 hover:bg-white/[0.04]'}`}
                >
                  <span className={`font-heading text-xl ${isSelected ? 'text-primary' : 'text-white/70'}`}>
                    {n}
                  </span>
                  <span className={`text-[9px] uppercase tracking-wider ${isSelected ? 'text-white/80' : 'text-muted-foreground'}`}>
                    {n === 1 ? 'day' : 'days'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Dietary Preferences Section */}
        <div className="pb-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 mb-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_currentColor]" />
            <h4 className="font-heading text-xs sm:text-sm text-white uppercase tracking-[0.2em] flex items-center gap-2">
              <Leaf className="h-3.5 w-3.5 text-emerald-400" />
              Dietary Preferences
            </h4>
          </div>
          <p className="text-muted-foreground text-sm mb-4">
            Select any restrictions — your AI nutrition plans and grocery lists will automatically respect them.
          </p>
          <div className="flex flex-wrap gap-2">
            {dietaryOptions.map((opt) => {
              const isSelected = dietaryRestrictions.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onToggleDietaryRestriction(opt.id)}
                  data-testid={`profile-diet-${opt.id}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium border transition-all
                    ${isSelected 
                      ? 'bg-emerald-500/[0.1] border-emerald-500/40 text-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.08)]' 
                      : 'bg-white/[0.02] border-white/10 text-muted-foreground hover:border-emerald-500/25 hover:text-white'}`}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="mt-4 space-y-2">
            <Label htmlFor="custom-diet" className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
              <Utensils className="h-4 w-4 text-primary" />
              Custom restrictions / allergies
            </Label>
            <Textarea
              id="custom-diet"
              value={customDietaryNotes || ''}
              onChange={(e) => onCustomDietaryNotesChange(e.target.value)}
              placeholder='e.g. "no shellfish, no soy, low sodium, hate cilantro"'
              className="min-h-[80px] bg-black/60 border-white/10 focus:border-primary text-white resize-none rounded-lg"
              maxLength={400}
              data-testid="profile-custom-diet-input"
            />
            <p className="text-xs text-muted-foreground text-right">{(customDietaryNotes || '').length}/400 characters</p>
          </div>
        </div>

        {/* Goal Details Section */}
        <div className="pb-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_currentColor]" />
            <h4 className="font-heading text-xs sm:text-sm text-white uppercase tracking-[0.2em]">Goal Details</h4>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="fitness_goal" className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                Your Fitness Goal
              </Label>
              <Input
                id="fitness_goal"
                name="fitness_goal"
                value={formData.fitness_goal}
                onChange={(e) => onFormChange({ ...formData, fitness_goal: e.target.value })}
                placeholder="e.g., Lose 20 lbs, Build muscle, Run a marathon"
                className="h-12 bg-black/60 border-white/10 focus:border-primary text-white rounded-lg"
                data-testid="profile-goal-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                Goal Deadline
              </Label>
              <Popover open={calendarOpen} onOpenChange={onCalendarOpenChange}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-12 justify-start text-left font-normal bg-black/60 border-white/10 hover:bg-white/[0.04] hover:border-primary/30 text-white rounded-lg"
                    data-testid="profile-deadline-btn"
                  >
                    <CalendarDays className="mr-2 h-4 w-4 text-primary" />
                    {goalDate ? format(goalDate, 'MMMM d, yyyy') : 'Select target date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                  <Calendar
                    mode="single"
                    selected={goalDate}
                    onSelect={onDateSelect}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {goalDate && daysUntilGoal > 0 && (
            <div className="mt-4 relative rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/[0.1] via-primary/[0.04] to-transparent p-4 flex items-center gap-3 overflow-hidden">
              <div className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
              <span className="relative h-9 w-9 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <Target className="h-4 w-4 text-primary" />
              </span>
              <p className="relative text-white/90 text-sm">
                You have <span className="font-heading text-primary text-lg">{daysUntilGoal} days</span> to reach your goal!
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 inline-flex items-center justify-center gap-2 h-12 text-sm font-semibold text-black bg-primary hover:bg-primary/90 rounded-full px-6 shadow-[0_4px_14px_rgba(250,204,21,0.25)] transition-all disabled:opacity-60 uppercase tracking-wider"
            data-testid="save-profile-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex items-center justify-center gap-2 h-12 text-sm font-semibold text-red-300 bg-red-500/[0.06] hover:bg-red-500/[0.12] border border-red-500/30 hover:border-red-500/50 rounded-full px-5 transition-all uppercase tracking-wider"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </form>
      </div>
    </motion.div>
  );
};
